<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/composables';
import { useAuthStore } from '@/stores/authStore';
import { LocaleEnum } from '@/types/enums/LocaleEnum';
import {
    BMenu, BListItem, BListItemTitle,
} from '@/design-system';
import type { VBtn } from 'vuetify/components';

type PropsType = {
    color?: string;
    density?: VBtn['$props']['density'];
};

withDefaults(defineProps<PropsType>(), { color: 'white', density: 'comfortable' });

const { localeRef, changeLanguage, getLanguages } = useTranslation();
const authStore = useAuthStore();
const isLoading = ref(false);

const languages = computed(() => getLanguages());

const currentLocale = computed(() => localeRef.value);

const handleLanguageChange = async (lang: LocaleEnum) => {
    if (isLoading.value) {
        return;
    }

    isLoading.value = true;

    try {
        await changeLanguage(lang);

        if (authStore.isAuthenticated && authStore.user) {
            await authStore.updateProfile({
                ...authStore.user,
                preferences: {
                    ...authStore.user.preferences,
                    language: lang,
                },
            });
        }
    } catch (error) {
        console.error('Failed to change language:', error);
    } finally {
        isLoading.value = false;
    }
};
</script>

<template>
    <BMenu
        activator-icon="mdi-translate"
        activator-btn-density="comfortable"
        :activator-icon-clr="color"
        activator-btn-rounded="circle"
    >
        <template #list-items>
            <BListItem
                v-for="language in languages"
                :key="language.value"
                :active="currentLocale === language.value"
                :disabled="isLoading"
                @click="() => handleLanguageChange(language.value as LocaleEnum)"
            >
                <BListItemTitle>
                    {{ language.flag }} {{ language.name }}
                    <span
                        v-if="isLoading && currentLocale === language.value"
                        class="ml-2"
                    >...</span
                    >
                </BListItemTitle>
            </BListItem>
        </template>
    </BMenu>
</template>
